import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonItem,
  IonIcon,
  IonButtons,
  IonButton,
  IonModal,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonActionSheet,
} from '@ionic/react';
import {
  radioOutline,
  navigateOutline,
  addOutline,
  closeOutline,
  qrCodeOutline,
  mapOutline,
  star,
  starOutline,
  wifiOutline,
} from 'ionicons/icons';
import { useAuth } from '../../application/use-cases/auth/AuthContext';
import { wifiQuality } from '../../domain/Hub';
import { enabledOfKind, hasComponent } from '../../domain/Component';
import type { Device } from '../../domain/Device';
import type { StationNow } from '../../domain/Lot';
import { useDevicesPage } from '../../application/use-cases/devices/useDevicesPage';
import QrScannerModal from '../components/QrScannerModal';
import LocationPicker from '../components/map/LocationPicker';
import ComponentPicker from '../components/station/ComponentPicker';
import './Sensors.css';

const round1 = (n: number) => Math.round(n * 10) / 10;

// Todos los indicadores que tiene la estación, abreviados para la tarjeta;
// "—" si todavía no mandó ese dato.
function sensorValues(device: Device, now: StationNow | undefined) {
  const has = (kind: Parameters<typeof hasComponent>[1]) => hasComponent(device.components, kind);
  const values: { label: string; value: string; unit?: string }[] = [];
  const num = (r: { value: number } | null | undefined, fmt: (v: number) => string) => (r ? fmt(r.value) : '—');
  if (has('air')) {
    values.push({ label: 'Temp.', value: num(now?.temperature, (v) => `${round1(v)}°`) });
    values.push({ label: 'Hum.', value: num(now?.humidity, (v) => `${Math.round(v)}%`) });
  }
  if (has('pressure')) values.push({ label: 'Presión', value: num(now?.pressure, (v) => `${Math.round(v)}`), unit: 'hPa' });
  if (has('wind')) values.push({ label: 'Viento', value: num(now?.windSpeed, (v) => `${Math.round(v)}`), unit: 'km/h' });
  if (has('rain')) {
    const rain = now?.rainToday;
    values.push({ label: 'Lluvia', value: rain !== null && rain !== undefined ? `${round1(rain)}` : '—', unit: 'mm' });
  }
  const probes = enabledOfKind(device.components, 'soil');
  for (const probe of probes) {
    const reading = now?.soil.find((p) => p.channel === probe.channel);
    values.push({ label: probes.length > 1 ? `Suelo ${probe.channel}` : 'Suelo', value: num(reading, (v) => `${Math.round(v)}%`) });
  }
  return values;
}

const Devices: React.FC = () => {
  const {
    devices,
    visibleDevices,
    now,
    lots,
    lotFilter,
    setLotFilter,
    sites,
    loading,
    error,
    showScanner,
    setShowScanner,
    showAdd,
    setShowAdd,
    newName,
    setNewName,
    newComponents,
    setNewComponents,
    newSiteId,
    setNewSiteId,
    newLat,
    newLng,
    setNewLocation,
    handleUseMyLocation,
    locating,
    creating,
    formError,
    handleScan,
    hubs,
    showAddMenu,
    setShowAddMenu,
    goToHub,
    goToClaimHub,
    goToAddStation,
    openAdd,
    handleCreate,
    handleToggleFavorite,
    goToDevice,
    goToMap,
  } = useDevicesPage();
  const { user } = useAuth();

  const selectedSite = sites.find((s) => s.id === newSiteId) ?? null;
  const pickerCenter: [number, number] | undefined =
    selectedSite?.lat !== null && selectedSite?.lat !== undefined && selectedSite?.lng !== null && selectedSite?.lng !== undefined
      ? [selectedSite.lat, selectedSite.lng]
      : undefined;

  const lotName = (id: string | null) => lots.find((l) => l.id === id)?.name ?? null;

  return (
    <IonPage>
      <IonHeader className="sensors-header">
        <IonToolbar>
          <IonTitle>
            <span className="sensors-title">
              <IonIcon icon={radioOutline} />
              Sensores
            </span>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={goToMap} aria-label="Mapa">
              <IonIcon icon={mapOutline} />
            </IonButton>
            <IonButton onClick={() => setShowScanner(true)} aria-label="Escanear QR">
              <IonIcon icon={qrCodeOutline} />
            </IonButton>
            <button className="sensors-add" onClick={() => setShowAddMenu(true)} aria-label="Agregar equipo">
              <IonIcon icon={addOutline} />
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {!loading && !error && devices.length === 0 && (
          <IonText color="medium">
            <p className="ion-padding">
              Todavía no tenés estaciones. Tocá + y vinculá el hub de tu campo con el QR &quot;Vincular&quot; de su etiqueta: sus
              estaciones aparecen solas.
            </p>
          </IonText>
        )}

        {lots.length > 0 && (
          <div className="sensors-filter" role="tablist" aria-label="Filtrar por lote">
            <button role="tab" aria-selected={lotFilter === null} onClick={() => setLotFilter(null)}>
              Todos
            </button>
            {lots.map((l) => (
              <button key={l.id} role="tab" aria-selected={lotFilter === l.id} onClick={() => setLotFilter(l.id)}>
                {l.name}
              </button>
            ))}
          </div>
        )}

        {hubs.length > 0 && (
          <div className="sensors-list">
            {hubs.map((hub) => {
              const online = hub.status === 'online';
              const wifi = wifiQuality(hub.wifiRssi);
              return (
                <div
                  key={hub.id}
                  className={`sensor-card hub-card ${online ? '' : 'is-offline'}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => goToHub(hub.id)}
                >
                  <span className="sensor-card-icon">
                    <IonIcon icon={wifiOutline} />
                  </span>
                  <div className="sensor-card-body">
                    <div className="sensor-card-head">
                      <span className="sensor-card-name">
                        <strong>Conexión {hub.siteName ? `· ${hub.siteName}` : 'del campo'}</strong>
                      </span>
                      <span className={`sensor-card-status ${online ? 'is-online' : 'is-offline'}`}>
                        <span className="dot" />
                        {online ? 'En línea' : 'Sin conexión'}
                      </span>
                    </div>
                    <p className="sensor-card-shared">
                      Hub · {hub.deviceCount ?? 0} {hub.deviceCount === 1 ? 'estación' : 'estaciones'}
                      {wifi && ` · WiFi ${wifi}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="sensors-list">
          {visibleDevices.map((device) => {
            const online = device.status === 'online';
            const lot = lotName(device.lotId);
            const values = sensorValues(device, now[device.id]);
            return (
              <div
                key={device.id}
                className={`sensor-card ${online ? '' : 'is-offline'}`}
                role="button"
                tabIndex={0}
                onClick={() => goToDevice(device.id)}
              >
                <span className="sensor-card-icon">
                  <IonIcon icon={radioOutline} />
                </span>
                <div className="sensor-card-body">
                  <div className="sensor-card-head">
                    <span className="sensor-card-name">
                      <strong>{device.name}</strong>
                      {lot && <span> · {lot}</span>}
                    </span>
                    <span className={`sensor-card-status ${online ? 'is-online' : 'is-offline'}`}>
                      <span className="dot" />
                      {online ? 'Online' : 'Sin conexión'}
                    </span>
                  </div>
                  {device.access !== 'owner' && <p className="sensor-card-shared">Compartido por {device.ownerName}</p>}
                  <div
                    className="sensor-card-values"
                    style={{ '--cols': Math.min(Math.max(values.length, 1), 5) } as React.CSSProperties}
                  >
                    {values.map((v) => (
                      <span key={v.label}>
                        <strong>
                          {v.value}
                          {v.unit && v.value !== '—' && <small> {v.unit}</small>}
                        </strong>
                        <small>{v.label}</small>
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  className={`sensor-card-fav ${device.isFavorite ? 'is-favorite' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFavorite(device);
                  }}
                  aria-label={device.isFavorite ? 'Quitar como principal' : 'Mostrar primero en el inicio'}
                >
                  <IonIcon icon={device.isFavorite ? star : starOutline} />
                </button>
              </div>
            );
          })}
        </div>

        <IonModal isOpen={showAdd} onDidDismiss={() => setShowAdd(false)} initialBreakpoint={1} breakpoints={[0, 1]}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Agregar estación</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAdd(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <IonInput
                placeholder="Nombre (ej. Estación Lote 4)"
                value={newName}
                onIonInput={(e) => setNewName(e.detail.value ?? '')}
              />
            </IonItem>

            <p className="add-station-section">¿Qué tiene enchufado?</p>
            <ComponentPicker value={newComponents} onChange={setNewComponents} />
            <IonText color="medium">
              <p style={{ fontSize: 12.5, margin: '0 2px 8px' }}>
                Si no estás seguro, no pasa nada: cuando la estación mande un dato nuevo (por ejemplo una sonda más), se
                agrega solo. Lo podés cambiar después desde la estación.
              </p>
            </IonText>

            {sites.length > 0 && (
              <IonItem>
                <IonSelect
                  label="Campo"
                  placeholder="Sin asignar"
                  value={newSiteId}
                  onIonChange={(e) => setNewSiteId(e.detail.value)}
                >
                  {sites.map((site) => (
                    <IonSelectOption key={site.id} value={site.id}>
                      {site.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            )}

            <IonText color="medium">
              <p style={{ fontSize: 13, margin: '12px 2px 0' }}>
                ¿Dónde está instalada? De acá sale su clima y su pronóstico. Buscá la localidad, tocá el mapa, usá tu ubicación si estás
                al lado del equipo, o dejalo vacío si el equipo tiene GPS (la manda sola).
              </p>
            </IonText>
            <IonButton size="small" fill="outline" disabled={locating} onClick={handleUseMyLocation} style={{ marginTop: 8 }}>
              <IonIcon icon={navigateOutline} slot="start" />
              {locating ? 'Buscando…' : 'Usar mi ubicación actual'}
            </IonButton>
            <LocationPicker lat={newLat} lng={newLng} onChange={setNewLocation} center={pickerCenter} />

            {formError && (
              <IonText color="danger">
                <p>{formError}</p>
              </IonText>
            )}

            <IonButton expand="block" style={{ marginTop: 16 }} onClick={handleCreate} disabled={creating}>
              {creating ? 'Creando...' : 'Crear'}
            </IonButton>
          </IonContent>
        </IonModal>

        <QrScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)} onScan={handleScan} />

        <IonActionSheet
          isOpen={showAddMenu}
          onDidDismiss={() => setShowAddMenu(false)}
          header="Agregar equipo"
          buttons={[
            { text: 'Hub nuevo: escanear la etiqueta', handler: () => setShowScanner(true) },
            { text: 'Hub nuevo: tipear el código', handler: goToClaimHub },
            ...(hubs.length > 0 ? [{ text: 'Estación nueva', handler: goToAddStation }] : []),
            // Sin hub (simulador, pruebas): solo para nosotros.
            ...(user?.isAdmin ? [{ text: 'Estación manual (admin)', handler: openAdd }] : []),
            { text: 'Cancelar', role: 'cancel' },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Devices;
